import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  THEME_STORAGE_KEY,
  applyTheme,
  darkSchemeQuery,
  readThemePreference,
  resolveTheme,
  writeThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '../lib/theme';
import { ThemeContext } from './themeContext';

/**
 * Owns the theme after first paint. The inline script in `index.html` has
 * already set `data-theme` by the time this mounts; from here on a change of
 * preference, of the OS scheme (under `system`), or in another tab moves it.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readThemePreference);
  const [systemDark, setSystemDark] = useState(() => darkSchemeQuery()?.matches ?? false);

  const theme: ResolvedTheme =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : resolveTheme(preference);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // The OS scheme, followed live whatever the preference, so flipping to
  // `system` later needs no fresh read.
  useEffect(() => {
    const query = darkSchemeQuery();
    if (!query) return undefined;
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Another tab changed the preference: follow it.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) setPreferenceState(readThemePreference());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    writeThemePreference(next);
    setPreferenceState(next);
  }, []);

  const value = useMemo(
    () => ({ preference, theme, setPreference }),
    [preference, theme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
